/**
 * Shared copy + analytics locations for the primary "identify my grass" CTA.
 *
 * Mirrors the DIAGNOSE_CTA pattern in lib/blog-diagnose-cta.ts, but centralizes
 * DATA ONLY, not behavior. Identify is a hybrid interaction: on the homepage
 * every surface calls preventDefault() and smooth-scrolls to #analysis-card;
 * off-homepage the href actually navigates there. So each call site keeps its
 * own onClick handler — this module only unifies the label, the destination
 * fields (for cta_clicked instrumentation), and the per-surface cta_location
 * strings.
 *
 * IMPORTANT: the `location` values are the EXISTING PostHog cta_location strings,
 * preserved verbatim so historical breakdowns and funnels keep resolving. Do NOT
 * rename them — a rename flatlines every existing segment.
 *
 * The canonical `label` standardizes accidental drift ("Identify Your Grass",
 * "Identify My Grass", "Find out free", etc.). Three surfaces intentionally keep
 * their own context-tuned copy and do NOT use IDENTIFY_CTA.label: the Free-tier
 * pricing card ("Try free analysis"), the passive sticky sidebar ("Try it free"),
 * and the Scotts blog reframe ("Identify your grass first…"). Those still route
 * their cta_clicked through identifyCtaProps() with an explicit `text` so they
 * gain the destination instrumentation without losing their framing.
 */
export const IDENTIFY_CTA = {
  href: '/#analysis-card',
  label: 'Identify my grass',
  /** cta_clicked destination fields — parity with the app-download CTA instrumentation. */
  destination: 'grass_id',
  destinationUrl: '/#analysis-card',
  /**
   * Existing cta_location values, per surface. DO NOT rename (breaks historical analytics).
   *
   * IMPRESSION COVERAGE — deliberately partial, do not "complete" it blindly.
   * The scroll-dependent surfaces (sidebar, footer, blog inline, post-read, tool
   * result) fire `cta_surface_shown`, so their CTR is real. The persistent nav
   * surfaces below — `header`, `mobileHeader`, `mobileMenu`, `toolsMenu`,
   * `mobileToolsMenu` — deliberately do NOT, for two reasons:
   *
   *   1. They render on every page load, so their impression count would just be
   *      the pageview count. That denominator is already available from
   *      `$pageview` and needs no second event to duplicate it.
   *   2. Emitting one would add roughly 50k events/month of pure restatement.
   *
   * The problem an impression event WOULD have solved here is real but analytical,
   * not instrumental: clicks from these nav surfaces get pooled against other
   * surfaces' impressions whenever someone computes a page-level CTR without
   * splitting by `cta_location`. That is what produced the 20.8% "best bridge on
   * the site" figure in obsidian/research/funnel-decisions-2026-08.md, when the
   * best real per-location CTR on that page is ~1.2%. The fix is to always group
   * by `cta_location`, not to emit more events.
   *
   * Worth knowing when reading these numbers: on the site's #1 blog page over 28
   * days, `mobile_header_identify_grass` took 98 clicks and `header_identify_grass`
   * 22, while the purpose-built in-content picker took 5. Readers who want to
   * identify their grass reach for the nav, not the card.
   */
  location: {
    header: 'header_identify_grass',
    mobileHeader: 'mobile_header_identify_grass',
    mobileMenu: 'mobile_menu_identify_grass',
    toolsMenu: 'tools_menu_identify_grass',
    mobileToolsMenu: 'mobile_tools_menu_identify_grass',
    homepageMid: 'homepage_mid_cta',
    homepageFinal: 'homepage_final_cta',
    footer: 'footer_identify',
    sidebar: 'content_sidebar_identify_grass',
    pricingPreview: 'homepage_pricing_preview_free',
    blogInlineGrassType: 'blog_inline_grass_type_identify',
    blogInlineDefault: 'blog_inline_identify_grass',
    /** Post-read peak slot on identify-intent posts. New 2026-07-22 — the
     *  identify sibling of blog_post_read_diagnose, which had no equivalent. */
    blogPostReadIdentify: 'blog_post_read_identify',
    productsFooter: 'products_page_footer',
    /** Grass-picker escape hatch on the seeding calculator. New 2026-07-27 —
     *  the link existed untracked, so its clicks were invisible. */
    toolSeedingFormIdentify: 'tool_seeding_form_identify',
    /** Post-result tool → grass-ID handoff (components/tools/shared/tool-next-step-card.tsx).
     *  New 2026-07-27. ONE location for every tool; the per-tool breakdown rides
     *  on an additive `tool_id` property rather than a location per calculator. */
    toolResultIdentify: 'tool_result_identify',
    /**
     * Head-term landing page, /what-kind-of-grass-do-i-have. New 2026-08-23.
     *
     * The page shipped with two plain links into /#analysis-card and no
     * instrumentation at all, so its 28 clicks/28d had no funnel. The hero slot
     * is now the in-place photo picker and the closing slot is still a link, and
     * both report an impression. That makes the page a self-contained,
     * exposure-matched read on inline upload vs link CTA: same visitors, same
     * intent, one `cta_variant` property apart.
     */
    grassIdLandingHero: 'grass_id_landing_hero_identify',
    grassIdLandingFinal: 'grass_id_landing_final_identify',
    /**
     * Auto-linked prose phrase inside article body HTML ("identify your grass",
     * "what type of grass", ...), injected by lib/internal-links.ts. New
     * 2026-08-24.
     *
     * These anchors shipped pointing at `/` with no instrumentation, so their
     * clicks were invisible AND they landed the reader on the hero with the
     * uploader below the fold. They are a distinct surface from every inline
     * card, which matters because they sit on the same posts as the tracked
     * InlineScanCta: without their own location they silently absorb its credit
     * and make the inline-vs-link read unreadable.
     */
    blogProseIdentify: 'blog_prose_identify_link',
    /**
     * Exit-intent scan modal on identify-intent blog posts
     * (components/blog/exit-scan-modal.tsx), the scan-pitch arm of the
     * blog_exit_scan_modal_v1 experiment. New 2026-08-26. Shares the
     * newsletter modal's trigger and cooldown; its impressions fire
     * cta_surface_shown here, NOT newsletter_modal_shown.
     */
    blogExitScanModal: 'blog_exit_scan_modal',
  },
} as const

/**
 * Build the cta_clicked property bag for an identify CTA.
 *
 * Always populates `destination`/`destination_url` (the instrumentation gap this
 * closes — previously null on every identify CTA, unlike the app-download CTAs).
 * Pass an explicit `text` only for the three deliberately-framed surfaces; every
 * other call site omits it and gets the canonical label.
 */
export function identifyCtaProps(
  location: string,
  opts?: { text?: string },
): {
  cta_location: string
  cta_text: string
  destination: string
  destination_url: string
} {
  return {
    cta_location: location,
    cta_text: opts?.text ?? IDENTIFY_CTA.label,
    destination: IDENTIFY_CTA.destination,
    destination_url: IDENTIFY_CTA.destinationUrl,
  }
}
